/**
 * ExpensesTab — Notas de Gasto
 */
import { todayISO } from '../../../lib/utils'
import { useState, useEffect } from 'react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { useTenantLocale, formatMoneyForTenant } from '@/lib/locale'
import { fmtDate } from '@/lib/utils'
import { Plus, Loader2, Search, Receipt, CheckCircle, XCircle, FileText } from 'lucide-react'
import {
  useExpenses, useCreateExpense, useApproveExpense, useRejectExpense,
  useAccounts, useCostCenters,
  type ExpenseNote, type ChartOfAccount,
} from '../hooks/useAccounting'
import { KebabMenu, type KebabMenuItem } from '@/components/KebabMenu'
import { DataTable } from '@/components/ds/DataTable'
import { Pagination } from '@/components/ds/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { useNavigate } from 'react-router-dom'
import { usePermission } from '@/hooks/usePermission'
import { Modal } from '@/components/ds/Modal'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  approved: 'Aprobado',
  rejected: 'Rechazado',
}

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-surface-muted text-content-muted',
  approved: 'bg-success-surface text-success-content',
  rejected: 'bg-danger-surface text-danger-content',
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
}

function fmtCLP(value: string | number | undefined) {
  if (value === undefined || value === null) return '--'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '--'
  return formatMoneyForTenant(num)
}

// ─── Create / Edit Modal ────────────────────────────────────────────────────

interface ExpenseFormProps {
  accounts: ChartOfAccount[]
  costCenters: { id: string; code: string; name: string; is_active: boolean }[]
  onClose: () => void
}

function ExpenseForm({ accounts, costCenters, onClose }: ExpenseFormProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const [date, setDate] = useState(todayISO())
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [account, setAccount] = useState('')
  const [costCenter, setCostCenter] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('efectivo')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const create = useCreateExpense()

  const imputableAccounts = accounts.filter(a => a.is_imputable && a.is_active && a.account_type === 'expense')
  const activeCenters = costCenters.filter(c => c.is_active)

  const handleSave = () => {
    if (!date || !description || !amount || !account) return
    setError(null)
    create.mutate(
      { date, description, amount, account, cost_center: costCenter || null, payment_method: paymentMethod, reference, notes },
      {
        onSuccess: onClose,
        onError: (e: unknown) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setError(err.response?.data?.error?.message ?? 'Error al guardar')
        },
      },
    )
  }

  return (
    <Modal open onClose={onClose} title="Nueva Nota de Gasto" size="2xl"
      actions={<>
        <button onClick={onClose} className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft">
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={create.isPending || !date || !description || !amount || !account}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
        >
          {create.isPending && <Loader2 size={14} className="animate-spin" />}
          Crear Gasto
        </button>
      </>}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="expensestab-fecha" className="block text-sm font-medium text-content mb-1">Fecha *</label>
            <input id="expensestab-fecha" type="date" value={date} onChange={e => setDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
          </div>
          <div>
            <label htmlFor="expensestab-monto" className="block text-sm font-medium text-content mb-1">Monto *</label>
            <input id="expensestab-monto" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0"
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
          </div>
        </div>
        <div>
          <label htmlFor="expensestab-descripcion" className="block text-sm font-medium text-content mb-1">Descripcion *</label>
          <input id="expensestab-descripcion" value={description} onChange={e => setDescription(e.target.value)} placeholder="Descripción del gasto"
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="expensestab-cuenta-contable" className="block text-sm font-medium text-content mb-1">Cuenta Contable *</label>
            <EntityAutocomplete id="expensestab-cuenta-contable"
              options={imputableAccounts.map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))}
              value={account}
              onChange={setAccount}
              placeholder="Seleccionar cuenta"
              required
            />
          </div>
          <div>
            <label htmlFor="expensestab-centro-de-costo" className="block text-sm font-medium text-content mb-1">Centro de Costo</label>
            <EntityAutocomplete id="expensestab-centro-de-costo"
              options={activeCenters.map(c => ({ id: c.id, label: `${c.code} — ${c.name}` }))}
              value={costCenter}
              onChange={setCostCenter}
              placeholder="Sin centro de costo"
              allowClear
              clearLabel="Sin centro de costo"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="expensestab-metodo-de-pago" className="block text-sm font-medium text-content mb-1">Metodo de Pago</label>
            <select id="expensestab-metodo-de-pago" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200">
              {Object.entries(PAYMENT_METHOD_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="expensestab-referencia" className="block text-sm font-medium text-content mb-1">Referencia</label>
            <input id="expensestab-referencia" value={reference} onChange={e => setReference(e.target.value)} placeholder="N de boleta, factura, etc."
              className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200" />
          </div>
        </div>
        <div>
          <label htmlFor="expensestab-notas" className="block text-sm font-medium text-content mb-1">Notas</label>
          <textarea id="expensestab-notas" value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Notas adicionales (opcional)"
            className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200 resize-none" />
        </div>
        {error && (
          <div className="bg-danger-surface border border-danger-border rounded-lg px-3 py-2 text-sm text-danger-content">{error}</div>
        )}
      </div>
    </Modal>
  )
}

// ─── Main Tab ───────────────────────────────────────────────────────────────

export function ExpensesTab() {
  const locale = useTenantLocale()
  const navigate = useNavigate()
  const [filterStatus, setFilterStatus] = useState('')
  const [search, setSearch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)

  const [page, setPage] = useState(1)

  const params: Record<string, string | number> = { page }
  if (filterStatus) params.status = filterStatus
  if (search) params.search = search

  const { data, isLoading } = useExpenses(params as Parameters<typeof useExpenses>[0])
  const { data: accountsData } = useAccounts({ is_active: 'true' })
  const { data: ccData } = useCostCenters()
  const approveMut = useApproveExpense()
  const rejectMut = useRejectExpense()
  const { confirm, modalProps: confirmProps } = useConfirmModal()
  // Aprobar genera y aprueba el comprobante; rechazar no tiene vuelta atrás.
  // Por eso los dos piden confirmación (revisión QA 2026-10).
  const decidir = (mut: typeof approveMut, id: string) =>
    new Promise<void>(resolve => mut.mutate(id, { onSettled: () => resolve() }))

  const expenses = data?.expenses ?? []
  const accounts = accountsData?.accounts ?? []
  const costCenters = ccData?.cost_centers ?? []
  const { can } = usePermission()
  const canCreate = can('accounting.expenses', 'create')
  const canApprove = can('accounting.expenses', 'approve')

  const buildActions = (exp: ExpenseNote): KebabMenuItem[] => [
    {
      label: 'Aprobar',
      icon: <CheckCircle size={14} />,
      onClick: () => confirm({
        message: `¿Aprobar la nota de gasto «${exp.description}» por ${fmtCLP(exp.amount)}? Se genera y aprueba su comprobante contable, y la nota deja de ser editable.`,
        variant: 'success',
        confirmLabel: 'Aprobar',
        onConfirm: () => decidir(approveMut, exp.id),
      }),
      hidden: exp.status !== 'draft' || !canApprove,
      className: 'text-success-content-soft hover:bg-success-surface',
    },
    {
      label: 'Rechazar',
      icon: <XCircle size={14} />,
      onClick: () => confirm({
        message: `¿Rechazar la nota de gasto «${exp.description}»? Una nota rechazada no se puede volver a aprobar.`,
        variant: 'danger',
        confirmLabel: 'Rechazar',
        onConfirm: () => decidir(rejectMut, exp.id),
      }),
      hidden: exp.status !== 'draft' || !canApprove,
      className: 'text-danger-content-soft hover:bg-danger-surface',
    },
    {
      label: 'Ver voucher',
      icon: <FileText size={14} />,
      onClick: () => { if (exp.voucher) navigate(`/accounting/vouchers?id=${exp.voucher}`) },
      hidden: exp.status !== 'approved' || !exp.voucher,
    },
  ]

  return (
    <div>
      <ConfirmModal {...confirmProps} />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
            <input aria-label="Buscar gastos"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Buscar gastos..."
              className="border border-divider rounded-lg pl-9 pr-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary w-56"
            />
          </div>
          <select aria-label="Todos los estados"
            value={filterStatus}
            onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
            className="border border-divider rounded-lg px-3 py-1.5 text-sm text-content focus:ring-2 focus:ring-primary"
          >
            <option value="">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="approved">Aprobado</option>
            <option value="rejected">Rechazado</option>
          </select>
        </div>
        {canCreate && (
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover"
          >
            <Plus size={14} />Nuevo Gasto
          </button>
        )}
      </div>

      <DataTable
        rows={expenses}
        rowKey={exp => exp.id}
        loading={isLoading}
        minWidth={1020}
        caption="Notas de gasto"
        empty={
          <EmptyState
            icon={Receipt}
            title="Sin notas de gasto"
            action={canCreate ? (
              <button
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50"
              >
                <Plus size={14} />Registrar primer gasto
              </button>
            ) : undefined}
          />
        }
        columns={[
          {
            // ⚠️ Sin `sortable`: el endpoint pagina de a 25 y no acepta
            // `ordering`, asi que ordenar aca reordenaria solo la pagina
            // visible y el resultado mentiria sobre el total.
            key: 'date', header: 'Fecha', width: 110,
            render: exp => <span className="whitespace-nowrap">{fmtDate(exp.date, locale)}</span>,
          },
          {
            key: 'description', header: 'Descripción',
            render: exp => (
              <span className="block max-w-[200px] truncate" title={exp.description}>
                {exp.description}
              </span>
            ),
          },
          {
            key: 'amount', header: 'Monto', numeric: true, width: 130,
            render: exp => <span className="font-semibold whitespace-nowrap">{fmtCLP(exp.amount)}</span>,
          },
          {
            key: 'account', header: 'Cuenta',
            render: exp => (
              <span className="text-content-muted">
                <span className="font-mono text-xs">{exp.account_code}</span>
                <span className="text-content-subtle ml-1 text-xs">{exp.account_name}</span>
              </span>
            ),
          },
          {
            key: 'cost_center_name', header: 'Centro Costo',
            render: exp => exp.cost_center_name
              ? <span className="text-content-muted">{exp.cost_center_name}</span>
              : <span className="text-content-subtle">—</span>,
          },
          {
            key: 'payment_method', header: 'Metodo Pago',
            render: exp => (
              <span className="text-content-muted">
                {PAYMENT_METHOD_LABELS[exp.payment_method] || exp.payment_method}
              </span>
            ),
          },
          {
            key: 'status', header: 'Estado', width: 120,
            render: exp => (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[exp.status] ?? 'bg-surface-muted text-content-muted'}`}>
                {STATUS_LABELS[exp.status] ?? exp.status}
              </span>
            ),
          },
          {
            key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 60,
            render: exp => <div className="text-right"><KebabMenu items={buildActions(exp)} /></div>,
          },
        ]}
      />

      {/* 🔴 El paginador FALTABA: la vista devuelve de a 25 con `count` y
          `pages`, y la pantalla mostraba esa primera pagina como si fuera
          la lista completa. */}
      {(data?.pages ?? 1) > 1 && (
        <Pagination
          page={page}
          pageSize={data?.page_size ?? 25}
          total={data?.count ?? 0}
          onPageChange={setPage}
          locale={locale}
          className="mt-4"
        />
      )}

      {modalOpen && (
        <ExpenseForm
          accounts={accounts}
          costCenters={costCenters}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  )
}
